import { Global } from '@emotion/react';

import type { CSSResetProps } from '@chakra-ui/react';

const css = String.raw;

/**
 * Apply a CSS reset specifically to the scope of the Payment SDK's UI.
 *
 * Needed to ensure the parent application's styles don't affect the SDK's UI.
 *
 * @prop {string} scope - The CSS selector to apply the reset to. This matches
 *                        the root element of the SDK.
 */
export const CSSReset = ({ scope }: CSSResetProps) => (
  <Global
    styles={css`
      :where(${scope}) {
        line-height: var(--chakra-lineHeights-payment-sdk-body);
        -webkit-text-size-adjust: 100%;
        font-family: var(--chakra-fonts-payment-sdk-body), system-ui, sans-serif;
        -webkit-font-smoothing: antialiased;
        text-rendering: optimizeLegibility;
        -moz-osx-font-smoothing: grayscale;
        touch-action: manipulation;
        position: relative;
        min-height: 100%;
        margin: 0;
        font-feature-settings: 'kern';
      }

      :where(${scope}) *,
      :where(${scope}) *::after,
      :where(${scope}) *::before {
        border-color: transparent;
        border-style: solid;
        border-width: 0;
        box-sizing: border-box;
        word-wrap: break-word;
      }

      :where(${scope}) :is(*) {
        -webkit-tap-highlight-color: transparent;
      }

      :where(${scope}) main {
        display: block;
      }

      :where(${scope}) hr {
        border-top-width: 1px;
        box-sizing: content-box;
        height: 0;
        overflow: visible;
      }

      :where(${scope}) :where(pre, code, kbd, samp) {
        font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 1em;
      }

      :where(${scope}) a {
        background-color: transparent;
        color: inherit;
        text-decoration: underline;
      }

      :where(${scope}) abbr[title] {
        border-bottom: none;
        text-decoration: underline;
        -webkit-text-decoration: underline dotted;
        text-decoration: underline dotted;
      }

      :where(${scope}) :where(b, strong) {
        font-weight: bold;
      }

      :where(${scope}) small {
        font-size: 80%;
      }

      :where(${scope}) :where(sub, sup) {
        font-size: 75%;
        line-height: 0;
        position: relative;
        vertical-align: baseline;
      }

      :where(${scope}) sub {
        bottom: -0.25em;
      }

      :where(${scope}) sup {
        top: -0.5em;
      }

      $:where(${scope}) img {
        border-style: none;
      }

      :where(${scope}) :where(button, input, optgroup, select, textarea) {
        font-family: inherit;
        font-size: 100%;
        line-height: 1.15;
        margin: 0;
      }

      :where(${scope}) :where(button, input) {
        overflow: visible;
      }

      :where(${scope}) :where(button, select) {
        text-transform: none;
      }

      :where(${scope})
        :where(
          button::-moz-focus-inner,
          [type='button']::-moz-focus-inner,
          [type='reset']::-moz-focus-inner,
          [type='submit']::-moz-focus-inner
        ) {
        border-style: none;
        padding: 0;
      }

      :where(${scope}) fieldset {
        padding: 0.35em 0.75em 0.625em;
      }

      :where(${scope}) legend {
        box-sizing: border-box;
        color: inherit;
        display: table;
        max-width: 100%;
        padding: 0;
        white-space: normal;
      }

      :where(${scope}) progress {
        vertical-align: baseline;
      }

      :where(${scope}) textarea {
        overflow: auto;
      }

      :where(${scope}) :where([type='checkbox'], [type='radio']) {
        box-sizing: border-box;
        padding: 0;
      }

      :where(${scope}) input[type='number']::-webkit-inner-spin-button,
      :where(${scope}) input[type='number']::-webkit-outer-spin-button {
        -webkit-appearance: none !important;
      }

      :where(${scope}) input[type='number'] {
        -moz-appearance: textfield;
      }

      :where(${scope}) input[type='search'] {
        -webkit-appearance: textfield;
        outline-offset: -2px;
      }

      :where(${scope}) input[type='search']::-webkit-search-decoration {
        -webkit-appearance: none !important;
      }

      :where(${scope}) ::-webkit-file-upload-button {
        -webkit-appearance: button;
        font: inherit;
      }

      :where(${scope}) details {
        display: block;
      }

      :where(${scope}) summary {
        display: list-item;
      }

      :where(${scope}) template {
        display: none;
      }

      :where(${scope}) [hidden] {
        display: none !important;
      }

      :where(${scope})
        :where(blockquote, dl, dd, h1, h2, h3, h4, h5, h6, hr, figure, p, pre) {
        margin: 0;
      }

      :where(${scope}) button {
        background: transparent;
        padding: 0;
      }

      :where(${scope}) fieldset {
        margin: 0;
        padding: 0;
      }

      :where(${scope}) :where(ol, ul) {
        margin: 0;
        padding: 0;
      }

      :where(${scope}) textarea {
        resize: vertical;
      }

      :where(${scope}) :where(button, [role='button']) {
        cursor: pointer;
      }

      :where(${scope}) button::-moz-focus-inner {
        border: 0 !important;
      }

      :where(${scope}) table {
        border-collapse: collapse;
      }

      :where(${scope}) :where(h1, h2, h3, h4, h5, h6) {
        font-size: inherit;
        font-weight: inherit;
      }

      :where(${scope}) :where(button, input, optgroup, select, textarea) {
        padding: 0;
        line-height: inherit;
        color: inherit;
      }

      :where(${scope}) :where(input, textarea, button, select, a) {
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
      }

      :where(${scope})
        :where(img, svg, video, canvas, audio, iframe, embed, object) {
        display: block;
      }

      :where(${scope}) :where(img, video) {
        max-width: 100%;
        height: auto;
      }

      :where(${scope})
        [data-js-focus-visible]
        :focus:not([data-focus-visible-added]):not(
          [data-focus-visible-disabled]
        ) {
        outline: none;
        box-shadow: none;
      }

      :where(${scope}) select::-ms-expand {
        display: none;
      }

      :where(${scope}) .noscroll {
        overflow-y: hidden;
      }

      :where(${scope}) .scrollable {
        overflow-y: auto;
        overflow-x: hidden;

        &::-webkit-scrollbar {
          background-color: transparent;
          width: 8px;
        }

        &::-webkit-scrollbar-thumb {
          background-color: transparent;
          border-radius: 4px;
        }
      }

      :where(${scope}) .scrollable:hover {
        &::-webkit-scrollbar-thumb {
          background-color: #cccccc;
        }

        &::-webkit-scrollbar-thumb:hover {
          background-color: #898989;
        }
      }

      :where(${scope}) [data-rsbs-header] {
        padding: 0 !important;
      }

      :where(${scope}) .chakra-tooltip__arrow-wrapper {
        --popper-arrow-offset: -6px !important;
      }

      :where(${scope}) [data-popper-placement='bottom'] {
        .chakra-tooltip__arrow {
          border-top-width: 1px !important;
          border-left-width: 1px !important;
        }
      }

      :where(${scope}) [data-popper-placement='top'] {
        .chakra-tooltip__arrow {
          border-bottom-width: 1px !important;
          border-right-width: 1px !important;
        }
      }

      :where(${scope}) [data-popper-placement='right'] {
        .chakra-tooltip__arrow {
          border-bottom-width: 1px !important;
          border-left-width: 1px !important;
        }
      }

      :where(${scope}) [data-popper-placement='left'] {
        .chakra-tooltip__arrow {
          border-top-width: 1px !important;
          border-right-width: 1px !important;
        }
      }

      :where(${scope})
        :where(input:-webkit-autofill, input:-webkit-autofill:focus) {
        transition:
          background-color 600000s 0s,
          color 600000s 0s;
      }

      :where(${scope}) input[data-autocompleted] {
        background-color: transparent !important;
      }

      :where(${scope}) [data-focus-visible] {
        box-shadow: none !important;
      }
    `}
  />
);
